<script setup lang="ts">
/*
 * V1.1.4 — persistent "Resume Payroll" pill in the top bar.
 *
 * Visible only when (a) an In-Progress payroll run exists for the
 * tenant and (b) the user is not already on a `/pay/run/*` route. Click
 * navigates to the run shell with the active run's PayrollId.
 *
 * Prototype anchor: `renderResumePayrollPill()` (line 5368+) and
 * `#resumePayrollPillHost` slot in the top bar (line 1010) of
 * Optimal-HR-Payroll-Module.html.
 *
 * The richer subtext from the prototype ("paused at Step N · X min
 * ago") will be wired in alongside the run shell rework (P3).
 *
 * Data: the active run comes from `PayRepo.listPayrolls` (P0 data layer —
 * the mock repo on dev; no `/payroll-svc` call). In the prototype the pill
 * lives inside the embedded payroll module, which only renders at a
 * payroll-owning scope (Direct Workforce / Project, lead ruling Q6), so the
 * read is gated on the ownership context too: oversight scopes fire no
 * operational Pay read (QA-P0a-12).
 */
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { PlayCircle } from 'lucide-vue-next';
import { useActivePayrollRun } from '@/modules/pay/composables/useActivePayrollRun';
import { useOwnership } from '@/composables/useOwnership';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

/**
 * Only fetch the payroll list while the user is inside the Payroll module.
 * The pill lives in the always-mounted top bar, so an unconditional fetch hit
 * `GET /payroll-svc/employers/{id}/payrolls` on Home right after sign-in.
 * TanStack Query keeps the cached result when disabled, so once the user has
 * visited Payroll the pill can still surface globally from cache — without
 * ever firing the request on a fresh Home.
 */
const inPayModule = computed(() => route.path.startsWith('/pay'));
const { ctx } = useOwnership();
const owningScope = computed(() => ctx.value.canProcess);
const { activeRun, payrollId } = useActivePayrollRun({
  enabled: computed(() => inPayModule.value && owningScope.value),
});

/**
 * Hide on any `/pay/run/*` page — the prototype hides the pill once the
 * user is already inside the run shell.
 */
const onRunPage = computed(() => route.path.startsWith('/pay/run/'));

const visible = computed(
  () => owningScope.value && Boolean(activeRun.value && payrollId.value) && !onRunPage.value,
);

const runLabel = computed(() => activeRun.value?.Name ?? activeRun.value?.PayDate ?? '');

/**
 * The pill's accessible name. Below `lg` the visible label collapses to the icon, so the
 * name must not depend on the (then `display:none`) text.
 */
const accessibleName = computed(() => {
  const label = t('v2.shell.resume-pill.label');
  return runLabel.value ? `${label} · ${runLabel.value}` : label;
});

function onClick() {
  if (!payrollId.value) return;
  router.push({ name: 'pay-run', params: { payrollId: payrollId.value } });
}
</script>

<template>
  <button
    v-if="visible"
    type="button"
    class="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900 transition hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
    :title="$t('v2.shell.resume-pill.title')"
    :aria-label="accessibleName"
    data-testid="resume-payroll-pill"
    @click="onClick"
  >
    <PlayCircle class="h-3.5 w-3.5" aria-hidden="true" />
    <!-- Icon-only below lg so the top bar never scrolls the page sideways (Q13 / D2). -->
    <span class="hidden lg:inline" data-testid="resume-payroll-pill-label">
      {{ $t('v2.shell.resume-pill.label') }}
      <span v-if="runLabel" class="text-amber-900/80"> · {{ runLabel }}</span>
    </span>
  </button>
</template>
